import React from "react";
import {
  GraduationCap,
  Award,
  MapPin,
  ExternalLink,
  Calendar,
} from "lucide-react";
import { Education } from "../sample.data";
import { getVisibleSortedItems } from "../utils";

interface EducationListProps {
  education: Education[];
}

export const EducationList: React.FC<EducationListProps> = ({ education }) => {
  const visibleEducation = getVisibleSortedItems(education);

  if (visibleEducation.length === 0) return null;

  const getGpaProgress = (gpa: string) => {
    const numeric = parseFloat(gpa);
    if (isNaN(numeric)) return 0;
    return Math.min((numeric / 4.0) * 100, 100);
  };

  const getGpaColor = (gpa: string) => {
    const numeric = parseFloat(gpa);
    if (isNaN(numeric)) return "bg-gray-400";
    if (numeric >= 3.7) return "bg-emerald-500";
    if (numeric >= 3.3) return "bg-blue-500";
    if (numeric >= 3.0) return "bg-amber-500";
    return "bg-red-500";
  };

  return (
    <div className="space-y-6">
      {visibleEducation.map((edu, index) => (
        <article
          key={edu.id}
          className="group relative overflow-hidden rounded-2xl bg-white/80 backdrop-blur-xl border border-white/20 hover:bg-white/90 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/10"
        >
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-indigo-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Timeline Connection */}
          {index < visibleEducation.length - 1 && (
            <div className="absolute left-6 bottom-0 h-6 w-px bg-gradient-to-b from-blue-200 to-transparent" />
          )}

          <div className="relative p-4 sm:p-6">
            {/* Header */}
            <div className="flex items-start gap-3 sm:gap-4">
              {/* Institution Logo or Icon */}
              <div className="flex-shrink-0">
                {edu.logo ? (
                  <div className="relative">
                    <img
                      src={edu.logo}
                      alt={`${edu.school} logo`}
                      className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl object-contain bg-white shadow-sm ring-1 ring-gray-100"
                    />
                    <div className="absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 h-3 w-3 sm:h-4 sm:w-4 bg-blue-500 rounded-full flex items-center justify-center">
                      <GraduationCap className="h-2 w-2 sm:h-2.5 sm:w-2.5 text-white" />
                    </div>
                  </div>
                ) : (
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                    <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                )}
              </div>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base sm:text-lg text-gray-900 leading-tight">
                      {edu.school}
                    </h3>
                    <p className="text-sm sm:text-base text-blue-600 font-medium mt-0.5">
                      {edu.degree}
                    </p>
                  </div>

                  {/* Duration Badge */}
                  <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-gray-100 rounded-full text-xs sm:text-sm text-gray-600 self-start">
                    <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    <span className="font-medium whitespace-nowrap">
                      {edu.start} – {edu.end}
                    </span>
                  </div>
                </div>

                {/* Location */}
                {edu.location && (
                  <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-500">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>{edu.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* GPA Section */}
            {edu.gpa && (
              <div className="mt-4 p-4 bg-gray-50/80 rounded-xl border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Academic Performance
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {edu.gpa}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-1000 ${getGpaColor(edu.gpa)}`}
                    style={{ width: `${getGpaProgress(edu.gpa)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0.0</span>
                  <span>4.0</span>
                </div>
              </div>
            )}

            {/* Honors & Awards */}
            {edu.honors && edu.honors.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="h-4 w-4 text-amber-500" />
                  <span className="font-medium text-gray-900">
                    Honors & Awards
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {edu.honors.map((honor) => (
                    <span
                      key={honor}
                      className="px-3 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 rounded-full text-sm font-medium border border-amber-200 hover:from-amber-100 hover:to-orange-100 transition-colors duration-200"
                    >
                      {honor}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {edu.description && (
              <div className="mt-4 p-4 bg-blue-50/50 rounded-xl">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {edu.description}
                </p>
              </div>
            )}

            {/* Institution Link */}
            {edu.link && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <a
                  href={edu.link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Institution
                </a>
              </div>
            )}
          </div>
        </article>
      ))}
    </div>
  );
};
